/**
 * Tractor Out-of-Service (OOS) helper — client-side mirror of the SQL
 * functions `public.tractor_service_status(uuid, timestamptz)` and
 * `public.is_tractor_available_for_window(uuid, timestamptz, timestamptz)`.
 *
 * The server functions are the source of truth. This module exists for
 * optimistic UI updates and any local computation that has already fetched
 * `tractor_service_records`. Behavior MUST stay in lockstep with the SQL.
 *
 * Rules:
 * - `open` and `updated` are both active service items. `closed` is inert.
 * - Strict comparisons — no grace period.
 * - Priority (highest wins):
 *     oos_past_due   → any active record with window_end < now
 *     oos_indefinite → any active record with window_end IS NULL and window_start <= now
 *     oos_current    → any active record overlapping now
 *     upcoming_service → any active record with window_start > now
 *     available      → otherwise
 */

export type TractorServiceStatus =
  | "available"
  | "oos_current"
  | "oos_past_due"
  | "oos_indefinite"
  | "upcoming_service";

export const BLOCKING_OOS_STATUSES: ReadonlySet<TractorServiceStatus> = new Set([
  "oos_current",
  "oos_past_due",
  "oos_indefinite",
]);

export interface ServiceRecordLike {
  status: string | null;
  window_start: string | Date | null;
  window_end: string | Date | null;
}

function isOpenStatus(status: string | null | undefined): boolean {
  return status === "open" || status === "updated";
}

function toTime(v: string | Date | null | undefined): number | null {
  if (v === null || v === undefined) return null;
  const t = v instanceof Date ? v.getTime() : new Date(v).getTime();
  return Number.isFinite(t) ? t : null;
}

export function getTractorServiceStatus(
  records: readonly ServiceRecordLike[] | null | undefined,
  now: Date = new Date(),
): TractorServiceStatus {
  if (!records || records.length === 0) return "available";
  const nowT = now.getTime();

  let pastDue = false;
  let indefinite = false;
  let current = false;
  let upcoming = false;

  for (const r of records) {
    if (!isOpenStatus(r.status)) continue;
    const start = toTime(r.window_start);
    const end = toTime(r.window_end);
    if (start === null) continue;

    if (end !== null && end < nowT) {
      pastDue = true;
    } else if (end === null && start <= nowT) {
      indefinite = true;
    } else if (start <= nowT && (end === null || end >= nowT)) {
      current = true;
    } else if (start > nowT) {
      upcoming = true;
    }
  }

  if (pastDue) return "oos_past_due";
  if (indefinite) return "oos_indefinite";
  if (current) return "oos_current";
  if (upcoming) return "upcoming_service";
  return "available";
}

export function isTractorAvailableForWindow(
  records: readonly ServiceRecordLike[] | null | undefined,
  start: Date,
  end: Date,
): boolean {
  if (!records || records.length === 0) return true;
  const s = start.getTime();
  const e = end.getTime();

  for (const r of records) {
    if (!isOpenStatus(r.status)) continue;
    const rs = toTime(r.window_start);
    if (rs === null) continue;
    const re = toTime(r.window_end);
    const reEffective = re === null ? Number.POSITIVE_INFINITY : re;
    // half-open [start,end) overlap
    if (rs < e && reEffective > s) return false;
  }
  return true;
}

export function isBlockingOosStatus(status: TractorServiceStatus): boolean {
  return BLOCKING_OOS_STATUSES.has(status);
}